import type { NodeViewRenderer } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import type { EditorState, Transaction } from "@tiptap/pm/state";
import {
  addColumn,
  addRow,
  type CellIndex,
  deleteColumn,
  deleteRow,
  findCellIndex,
  toggleFirstColBold,
} from "./table-commands";
import { equalWidths, isUniform, resizeColumns, toPercentages } from "./table-math";

type TableCommand = (state: EditorState, pos: number, cell: CellIndex | null) => Transaction | null;
const KEY_STEP = 0.02;

export const tableView: NodeViewRenderer = ({ node: initial, editor, getPos }) => {
  let node: PMNode = initial;
  const editable = editor.isEditable;

  const dom = document.createElement("div");
  dom.className = "kd-table";
  const frame = document.createElement("div");
  frame.className = "kd-table__frame";
  const table = document.createElement("table");
  table.className = "kd-table__grid";
  const colgroup = document.createElement("colgroup");
  const tbody = document.createElement("tbody");
  table.append(colgroup, tbody);
  frame.append(table);
  const tools = document.createElement("div");
  tools.className = "kd-table__tools";
  tools.contentEditable = "false";
  const handles = document.createElement("div");
  handles.className = "kd-table__handles";
  handles.contentEditable = "false";
  let boldButton: HTMLButtonElement | null = null;

  const columnCount = () => node.firstChild?.childCount ?? 1;
  const widths = (): number[] => {
    const w = node.attrs.widths as number[] | null;
    return w && w.length === columnCount() ? w : equalWidths(columnCount());
  };
  const position = () => {
    const pos = getPos();
    return typeof pos === "number" ? pos : null;
  };
  const run = (command: TableCommand) => {
    const pos = position();
    if (pos === null || !editor.isEditable) return;
    const tr = command(editor.state, pos, findCellIndex(editor.state, pos));
    if (tr) editor.view.dispatch(tr);
    editor.view.focus();
  };
  const commit = (next: number[]) => {
    const pos = position();
    if (pos === null || !editor.isEditable) return;
    editor.view.dispatch(
      editor.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, widths: isUniform(next) ? null : next }),
    );
  };

  const makeHandle = (i: number) => {
    const h = document.createElement("div");
    h.className = "kd-table__handle";
    h.dataset.index = String(i);
    h.tabIndex = 0;
    h.setAttribute("role", "separator");
    h.setAttribute("aria-orientation", "vertical");
    h.setAttribute("aria-label", `Resize columns ${i + 1} and ${i + 2}`);
    return h;
  };
  const build = () => {
    const n = columnCount();
    if (colgroup.childElementCount !== n) {
      colgroup.replaceChildren(...Array.from({ length: n }, () => document.createElement("col")));
    }
    if (editable && handles.childElementCount !== n - 1) {
      handles.replaceChildren(...Array.from({ length: n - 1 }, (_, i) => makeHandle(i)));
    }
  };
  /** Positions only; never recreates elements, so a drag in progress keeps its pointer capture. */
  const layout = (current: number[]) => {
    const pcts = toPercentages(current);
    let left = 0;
    pcts.forEach((p, i) => {
      const col = colgroup.children[i] as HTMLElement | undefined;
      if (col) col.style.width = `${p}%`;
      if (i < pcts.length - 1) {
        left += p;
        const h = handles.children[i] as HTMLElement | undefined;
        if (h) h.style.left = `${left}%`;
      }
    });
  };
  const paint = () => {
    build();
    layout(widths());
    const bold = node.attrs.firstColBold !== false;
    dom.dataset.firstColBold = String(bold);
    boldButton?.setAttribute("aria-pressed", String(bold));
  };

  if (editable) {
    const tool = (label: string, name: string, onClick: () => void) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "kd-tool";
      b.textContent = label;
      b.setAttribute("aria-label", name);
      b.addEventListener("mousedown", (e) => e.preventDefault());
      b.addEventListener("click", onClick);
      return b;
    };
    boldButton = tool("Bold first column", "Bold first column", () => run((s, p) => toggleFirstColBold(s, p)));
    tools.append(
      tool("+ Row", "Add row", () => run(addRow)),
      tool("+ Column", "Add column", () => run(addColumn)),
      tool("− Row", "Delete row", () => run(deleteRow)),
      tool("− Column", "Delete column", () => run(deleteColumn)),
      boldButton,
    );
    dom.append(tools);
    frame.append(handles);

    handles.addEventListener("pointerdown", (event) => {
      const handle = (event.target as HTMLElement).closest<HTMLElement>(".kd-table__handle");
      if (!handle || !editor.isEditable) return;
      event.preventDefault();
      const index = Number(handle.dataset.index);
      const start = widths();
      const startX = event.clientX;
      const width = table.getBoundingClientRect().width || 1;
      let latest = start;
      handle.setPointerCapture?.(event.pointerId);
      dom.classList.add("is-resizing");
      const move = (e: PointerEvent) => {
        latest = resizeColumns(start, index, (e.clientX - startX) / width);
        layout(latest);
      };
      const end = () => {
        handle.removeEventListener("pointermove", move);
        handle.removeEventListener("pointerup", end);
        handle.removeEventListener("pointercancel", end);
        dom.classList.remove("is-resizing");
        if (latest !== start) commit(latest);
      };
      handle.addEventListener("pointermove", move);
      handle.addEventListener("pointerup", end);
      handle.addEventListener("pointercancel", end);
    });
    handles.addEventListener("keydown", (event) => {
      const handle = (event.target as HTMLElement).closest<HTMLElement>(".kd-table__handle");
      const step = event.key === "ArrowLeft" ? -KEY_STEP : event.key === "ArrowRight" ? KEY_STEP : 0;
      if (!handle || step === 0 || !editor.isEditable) return;
      event.preventDefault();
      commit(resizeColumns(widths(), Number(handle.dataset.index), step));
    });
  }
  dom.append(frame);
  paint();

  return {
    dom,
    contentDOM: tbody,
    update(next) {
      if (next.type !== node.type) return false;
      node = next;
      paint();
      return true;
    },
    stopEvent: (event) =>
      event.target instanceof Node && (tools.contains(event.target) || handles.contains(event.target)),
    ignoreMutation: (m) => m.type !== "selection" && !tbody.contains(m.target),
  };
};
